import { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { format, isEqual } from 'date-fns';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { SortingState } from '@tanstack/react-table';
import {
  RestaurantShift,
  ShortRestaurantLocation,
} from '@durma-soft/gros-sdk/dist/types/restaurants';
import {
  SHIFT,
  useAuthUserRestaurant,
  useGetAllRestaurantCompaniesQuery,
  useGetSubstituteMealsReportQuery,
} from '@durma-soft/gros-sdk';

import TableSizeSyncProvider from '@/providers/TableSizeSyncProvider';

import { useDatePickerRangeState } from '@/hooks/use-date-picker-range-state';

import { Button } from '@/components/shared/shadcn-ui/button';
import { PrintReportButton } from '@/components/shared/print-report-button';
import { Skeleton } from '@/components/shared/shadcn-ui/skeleton';
import { LastUpdatedInfo } from '@/components/shared/last-updated-info';
import { DataTable, DataTableHTML } from '@/components/shared/data-table';
import { DatePickerWithRange } from '@/components/shared/date-picker-with-range';
import { SelectCompanyLocations } from '@/components/shared/select-company-locations';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/shared/shadcn-ui/dropdown-menu';

import { columns } from '@/config/restaurant/reports/packages';
import { repeat } from '@/utils/helpers';
import print from '@/lib/print/print';

const SubstituteMealsReport = () => {
  const { t } = useTranslation();
  const memoizedColumns = useMemo(() => columns(t), [t]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [shiftsDropdownOpen, setShiftsDropdownOpen] = useState(false);
  const [companyLocations, setCompanyLocations] = useState<
    ShortRestaurantLocation[]
  >([]);
  const [selectedShifts, setSelectedShifts] = useState<RestaurantShift[]>([
    SHIFT.FIRST,
    SHIFT.SECOND,
    SHIFT.THIRD,
  ]);

  const onUpdateCompanyLocations = (
    newCompanyLocations: ShortRestaurantLocation[],
  ) => {
    setCompanyLocations(newCompanyLocations);
  };

  const restaurant = useAuthUserRestaurant();

  const { from, to, setDateRange } = useDatePickerRangeState();
  const {
    data: companiesAndGroups,
    isLoading: isLoadingCompanies,
    isSuccess: isSuccessCompanies,
  } = useGetAllRestaurantCompaniesQuery(restaurant.id);

  const {
    data: substituteMealOrders,
    isLoading: isLoadingSubstituteMealOrders,
    isSuccess: isSuccessSubsituteMealOrders,
    isRefetching,
    dataUpdatedAt,
    refetch,
  } = useGetSubstituteMealsReportQuery({
    restaurant_id: restaurant.id,
    start_date: from,
    end_date: to,
    company_location_ids: companyLocations.length
      ? companyLocations.map(({ id }) => id)
      : [],
  });

  const handleRefetch = async () => {
    await refetch();
  };

  return (
    <>
      <Helmet>
        <title>{t('reports.substitute-meals.title')}</title>
      </Helmet>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold md:text-2xl">
          {t('reports.substitute-meals.title')}
        </h1>
        {(isLoadingCompanies || isLoadingSubstituteMealOrders) && (
          <Skeleton className="h-10 w-52" />
        )}
        {isSuccessCompanies && isSuccessSubsituteMealOrders && (
          <LastUpdatedInfo
            dataUpdatedAt={dataUpdatedAt}
            isRefetching={isRefetching}
            handleRefetch={handleRefetch}
          />
        )}
      </div>
      <div className="flex flex-wrap justify-between gap-5 my-5">
        <div className="flex flex-col xl:flex-row gap-5">
          <DatePickerWithRange
            date={{
              from: new Date(from),
              to: new Date(to),
            }}
            setDate={([from, to]) => {
              setDateRange({ from, to });
            }}
          />
          <DropdownMenu
            open={shiftsDropdownOpen}
            onOpenChange={setShiftsDropdownOpen}
          >
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="gap-2 font-normal print:hidden flex justify-between w-[200px]"
              >
                {t('common.choose-shifts')}
                <span>
                  <ChevronDown size={16} />
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {['1', '2', '3'].map((shift) => (
                <DropdownMenuCheckboxItem
                  key={shift}
                  checked={selectedShifts.includes(shift as RestaurantShift)}
                  onSelect={(event) => {
                    event.preventDefault();
                    setSelectedShifts((prev) =>
                      prev.includes(shift as RestaurantShift)
                        ? prev.filter((s) => s !== shift)
                        : [...prev, shift as RestaurantShift],
                    );
                  }}
                >
                  {shift}. {t('menu.shift')}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <SelectCompanyLocations
            companies={companiesAndGroups?.companies || []}
            groups={companiesAndGroups?.groups || []}
            isLoading={isLoadingCompanies}
            selectedCompanyLocations={companyLocations}
            onUpdateCompanyLocations={onUpdateCompanyLocations}
          />
        </div>
        <div>
          <PrintReportButton
            disabled={isLoadingCompanies || isLoadingSubstituteMealOrders}
            onPrint={(fontScale) =>
              print({
                fontScale,
                content: (
                  <div>
                    <div className="flex-between">
                      <h1>{t('reports.substitute-meals.title')} </h1>
                      <div className="flex-row">
                        <p>{t('common.data-updated-at')}</p>
                        <span>&nbsp;</span>
                        <p>
                          <span> {format(dataUpdatedAt, 'dd.MM.yyyy')}</span>
                          <span> {t('common.in')} </span>
                          <span> {format(dataUpdatedAt, 'HH:mm:ss')}</span>
                        </p>
                      </div>
                    </div>
                    <div>
                      {isSuccessSubsituteMealOrders &&
                        substituteMealOrders.map((order) => {
                          return (
                            <div
                              key={order.id}
                              className="flex flex-col gap-4 print-in-separate-page"
                            >
                              <p>
                                {format(from, 'dd.MM.yyyy')}
                                {!isEqual(from, to) &&
                                  ` - ${format(to, 'dd.MM.yyyy')}`}
                              </p>
                              <h3 className="text-xl font-medium">
                                {order.name}
                              </h3>
                              <DataTableHTML
                                data={order.foods}
                                sorting={sorting}
                                setSorting={setSorting}
                                columns={memoizedColumns}
                                columnVisibility={{
                                  shift_1: selectedShifts.includes(SHIFT.FIRST),
                                  shift_2: selectedShifts.includes(
                                    SHIFT.SECOND,
                                  ),
                                  shift_3: selectedShifts.includes(SHIFT.THIRD),
                                }}
                              />
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ),
              })
            }
          />
        </div>
      </div>
      <div className="flex flex-col gap-3">
        {(isLoadingCompanies || isLoadingSubstituteMealOrders) && (
          <div className="flex flex-col gap-10">
            {repeat(3).map((index) => (
              <div key={index}>
                <Skeleton className="w-full rounded-md h-60" />
              </div>
            ))}
          </div>
        )}
        {isSuccessSubsituteMealOrders &&
          !substituteMealOrders.length &&
          companyLocations.length !== 0 && (
            <p className="self-center">{t('reports.no-data')}</p>
          )}

        {companyLocations.length === 0 && (
          <p className="self-center">{t('reports.orders.choose-location')}</p>
        )}
        {isSuccessSubsituteMealOrders && (
          <TableSizeSyncProvider>
            {substituteMealOrders.map((order, index) => {
              return (
                <div key={order.id} className="flex flex-col gap-4">
                  <h3 className="text-xl font-medium">{order.name}</h3>
                  <DataTable
                    data={order.foods}
                    sorting={sorting}
                    setSorting={setSorting}
                    columns={memoizedColumns}
                    isSyncReference={index === 0}
                    columnVisibility={{
                      shift_1: selectedShifts.includes(SHIFT.FIRST),
                      shift_2: selectedShifts.includes(SHIFT.SECOND),
                      shift_3: selectedShifts.includes(SHIFT.THIRD),
                    }}
                  />
                </div>
              );
            })}
          </TableSizeSyncProvider>
        )}
      </div>
    </>
  );
};

export default SubstituteMealsReport;
